import { Badge } from "@thilke/ui/components/badge";
import { Panel } from "@thilke/ui/components/panel";
import { ScrollArea } from "@thilke/ui/components/scroll-area";
import { BadgeOverflow } from "@thilke/ui/components/ui/badge-overflow";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface JournalReference {
  id: string;
  label: string;
  kind?: string;
}

export interface JournalKbSheetProps extends React.ComponentProps<"div"> {
  title: string;
  subtitle?: string;
  tags?: string[];
  references?: JournalReference[];
}

function JournalKbSheet({
  title,
  subtitle,
  tags = [],
  references = [],
  children,
  className,
  ...props
}: JournalKbSheetProps) {
  return (
    <Panel
      position="docked"
      size="md"
      className={cn("grid gap-2.5", className)}
      {...props}
    >
      <header className="grid gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <h2 className="text-sm font-semibold leading-tight">{title}</h2>
          {tags.length > 0 ? (
            <BadgeOverflow
              items={tags}
              lineCount={1}
              className="flex min-w-0 flex-1 flex-wrap gap-1.5"
              renderBadge={(tag) => (
                <Badge variant="outline" className="shrink-0">
                  {tag}
                </Badge>
              )}
              renderOverflow={(count) => (
                <Badge variant="secondary" className="shrink-0">
                  +{count}
                </Badge>
              )}
            />
          ) : null}
        </div>
        {subtitle ? (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        ) : null}
      </header>
      <ScrollArea className="h-48 rounded-md border border-border bg-card/80">
        <div className="grid gap-1 p-3 text-xs leading-normal text-foreground">
          {children}
        </div>
      </ScrollArea>
      {references.length > 0 ? (
        <footer className="grid gap-1 border-t border-border pt-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            linked references
          </div>
          <div className="flex flex-wrap gap-1.5">
            {references.map((reference) => (
              <Badge
                key={reference.id}
                variant="secondary"
                className="normal-case tracking-normal"
              >
                {reference.kind ? `${reference.kind}: ` : ""}
                {reference.label}
              </Badge>
            ))}
          </div>
        </footer>
      ) : null}
    </Panel>
  );
}

export { JournalKbSheet };
